/**
 * THE SIX-DIGIT CODE FROM THE EMAIL — the fallback that was printed but never accepted.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS SCREEN HAD TO BE BUILT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Every transactional template renders `{{ .Token }}` under "Button not working? Sign in
 * with this code instead" — `invite.html:169`, and the same block in recovery, magic-link
 * and confirmation. Nothing in the app accepted it: there was no call to `verifyOtp`
 * anywhere in the tree, and the sign-in state machine had no code entry outside MFA. So the
 * one recipient the fallback exists for — behind a corporate gateway that rewrites or
 * breaks the link, which is the ordinary case in the utilities and telecoms this product is
 * sold into — could not complete their invitation at all.
 *
 * The code and the link are the same credential: GoTrue's `/verify` accepts either. This
 * screen is therefore not a lesser path, it is the same one typed by hand.
 *
 * The address is asked for because `verifyOtp` requires it — the six digits alone do not
 * identify an account. It is not a second factor and is not treated as one.
 *
 * Visual language is `MfaScreen`'s, deliberately: same slot geometry, same chromatic
 * aberration, same one-real-input-behind-the-slots trick that keeps paste, autofill and
 * mobile keyboards working where six separate inputs break them.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { humanError } from '@/lib/human-error';

const CODE_LENGTH = 6;

/** The three link kinds a person can be sent. `signup` is unreachable: sign-up is disabled. */
export type CodePurpose = 'invite' | 'recovery' | 'magiclink';

/**
 * What the screen says once the credential is spent.
 *
 * The ordinary copy below sells the six-digit code as the way through when the link fails.
 * That is exactly right for a mangled link and exactly wrong for an expired one, where the
 * code died with it — so the page would offer the code in its subtitle and withdraw it two
 * lines later.
 */
const EXPIRED_COPY: Record<CodePurpose, { h1: string; sub: string }> = {
  invite: {
    h1: 'That invitation has expired.',
    sub: 'Invitation links are valid for 24 hours.',
  },
  recovery: {
    h1: 'That reset link has expired.',
    sub: 'Reset links are valid for 24 hours and can only be used once.',
  },
  magiclink: {
    h1: 'That sign-in link has expired.',
    sub: 'Sign-in links are valid for 24 hours and can only be used once.',
  },
};

const COPY: Record<CodePurpose, { h1: string; sub: string; cta: string }> = {
  invite: {
    h1: 'Let’s get you set up.',
    sub: 'Enter the six-digit code from your invitation email. Use it when the button in the email does not work.',
    cta: 'Accept invitation',
  },
  recovery: {
    h1: 'Enter your reset code.',
    sub: 'The six-digit code in the reset email works wherever the link does not. You will choose a new password next.',
    cta: 'Continue',
  },
  magiclink: {
    h1: 'Enter your sign-in code.',
    sub: 'The six-digit code from the email you were just sent.',
    cta: 'Sign in',
  },
};

export function CodeScreen({
  purpose,
  email,
  onEmail,
  notice,
  expired = false,
  onRequestNew,
  onAttempt,
  onRejected,
  onBack,
}: {
  purpose: CodePurpose;
  email: string;
  onEmail: (v: string) => void;
  /** Why this screen is showing rather than the password form — an expired link, usually. */
  notice?: string | null;
  /**
   * The credential is SPENT, so there is nothing to type.
   *
   * GoTrue issues one credential per email and the six-digit code IS that credential — it
   * dies with the link. When the link failed because it expired or was already used, showing
   * the code entry offers a person the one thing that cannot possibly work, and this screen
   * had no other action on it. A malformed or truncated link is the opposite case, and the
   * one this screen was written for: there the code is still good.
   */
  expired?: boolean;
  /**
   * Send a fresh email. Absent when there is no self-service route — an invitation is sent
   * by an administrator, so the recipient genuinely cannot request another and the screen
   * says so rather than offering a button that would lie.
   */
  onRequestNew?: (() => void) | undefined;
  /**
   * Fired as the code is submitted, BEFORE the session can land.
   *
   * Not a nicety: `verifyOtp` notifies the auth subscribers before its own promise resolves,
   * so the gate can swap the tree while this component is still awaiting. Registering the
   * intent first is what stops an invited user flashing past the set-password screen into
   * the console.
   */
  onAttempt: () => void;
  /** The code was refused, so nothing was signed in and the intent must be given back. */
  onRejected: () => void;
  onBack: () => void;
}): ReactNode {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  const address = email.trim();
  const ready = address.length > 0 && code.length === CODE_LENGTH && !busy;
  const copy = COPY[purpose];

  /*
   * The heading and subtitle both SELL the six-digit code — "works wherever the link does
   * not" — which is true for the case this screen was built for and a flat contradiction
   * once the credential has expired. Leaving them would have the page offer the code in one
   * sentence and withdraw it in the next.
   */
  const head = expired ? EXPIRED_COPY[purpose] : copy;

  const slots = useMemo(
    () =>
      Array.from({ length: CODE_LENGTH }, (_, i) => ({
        char: code[i] ?? '',
        filled: i < code.length,
        caret: i === code.length,
      })),
    [code],
  );

  async function verify(value: string): Promise<void> {
    if (address.length === 0 || value.length !== CODE_LENGTH || busy) return;
    setBusy(true);
    setError(null);
    onAttempt();

    const { error: err } = await supabase.auth.verifyOtp({
      email: address,
      token: value,
      type: purpose,
    });

    if (err) {
      onRejected();
      setError(humanise(err.message));
      setCode('');
      input.current?.focus();
      setBusy(false);
      return;
    }

    /* On success the session lands and the gate swaps the whole tree, so this stays busy
       until it unmounts; setting a "done" state would race that unmount. */
  }

  return (
    <section style={{ width: '100%', maxWidth: 420, animation: RISE }}>
      <h1 style={h1}>{head.h1}</h1>
      <p style={sub}>{head.sub}</p>

      {/*
        * The generic notice is suppressed once the dedicated expired copy is showing. It
        * says the same thing a third time — the heading states the link expired, the
        * subtitle gives the window, and the action below names the way out. Three sentences
        * for one fact reads as a screen that is not sure it was heard.
        */}
      {notice && !expired && (
        <p role="status" style={noticeNote}>
          {notice}
        </p>
      )}

      {/*
        * A SPENT CREDENTIAL GETS A WAY FORWARD, NOT A FORM.
        *
        * The code entry below is correct for a link a mail client mangled — the code in the
        * same email still works. It is wrong for an expired or already-used link, because
        * GoTrue issues ONE credential per email and the code is that credential. Offering it
        * asks the reader to type the only thing guaranteed to fail, and this screen used to
        * be the end of the road when they did.
        */}
      {expired ? (
        <div style={{ marginTop: 30, display: 'grid', gap: 16 }}>
          {onRequestNew ? (
            <>
              <p style={{ ...sub, margin: 0 }}>
                The code in that email has expired with the link. Send yourself a new one.
              </p>
              <button
                type="button"
                className="lm-si-amber lm-press"
                onClick={onRequestNew}
                /* The same primary treatment as this screen's own submit, inline for the
                   same reason it is inline there: `lm-si-btn` does not exist, and a class
                   that resolves to nothing renders a link-shaped nothing where the page's
                   only action should be. */
                style={{
                  width: '100%',
                  minHeight: 48,
                  border: 0,
                  borderRadius: 10,
                  font: 'inherit',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#18181b',
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  boxShadow: '0 6px 26px rgba(245,158,11,0.3)',
                  cursor: 'pointer',
                }}
              >
                Send a new reset link
              </button>
            </>
          ) : (
            <p style={{ ...sub, margin: 0 }}>
              Invitations are sent by an administrator at your organisation. Ask them to send
              a new one — the code in the expired email will not work.
            </p>
          )}
          <a
            className="lm-si-link"
            href="#"
            style={{ fontSize: 13 }}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            Back to sign in
          </a>
        </div>
      ) : (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void verify(code);
        }}
        style={{ display: 'grid', gap: 18, marginTop: 30 }}
      >
        <label style={{ display: 'grid', gap: 7 }}>
          <span style={labelStyle}>Email</span>
          <input
            className="lm-si-input"
            type="email"
            autoComplete="username"
            placeholder="you@operator.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            style={inputStyle}
            autoFocus={email.length === 0}
          />
        </label>

        <div style={{ display: 'grid', gap: 7 }}>
          <span style={labelStyle}>Code from the email</span>
          <div style={{ position: 'relative', width: 'fit-content' }}>
            {/* One real input behind the slots — see MfaScreen. */}
            <input
              ref={input}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Six-digit code from the email"
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                setCode(next);
                /* Auto-submit only once the address is there — verifyOtp cannot identify an
                   account from six digits, and a request that is certain to fail would burn
                   one of GoTrue's limited attempts on this code. */
                if (next.length === CODE_LENGTH && address.length > 0) void verify(next);
              }}
              autoFocus={email.length > 0}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                border: 0,
                padding: 0,
                cursor: 'text',
                font: 'inherit',
              }}
            />
            <div onClick={() => input.current?.focus()} style={{ display: 'flex', gap: 8, cursor: 'text' }}>
              {slots.map((slot, i) => (
                <div
                  key={i}
                  style={{
                    width: 44,
                    height: 58,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    background: slot.filled ? '#141a26' : '#12161f',
                    border: `1px solid ${slot.caret ? '#36c5d6' : slot.filled ? '#2f3646' : '#283041'}`,
                    boxShadow: slot.caret ? '0 0 0 3px rgba(54,197,214,0.16)' : 'none',
                  }}
                >
                  {slot.filled && (
                    <div
                      style={{
                        position: 'relative',
                        fontWeight: 600,
                        fontSize: 28,
                        lineHeight: 1,
                        letterSpacing: '-0.03em',
                        fontFeatureSettings: "'tnum' 1",
                      }}
                    >
                      <span style={aberration('#f2a23c', -2, -1)}>{slot.char}</span>
                      <span style={aberration('#36c5d6', 2, 1)}>{slot.char}</span>
                      <span style={{ position: 'relative', color: '#fafafa' }}>{slot.char}</span>
                    </div>
                  )}
                  {slot.caret && (
                    <span
                      style={{
                        width: 2,
                        height: 26,
                        background: '#36c5d6',
                        boxShadow: '0 0 8px #36c5d6',
                        animation: 'lm-caret 1100ms steps(1, end) infinite',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <p role="alert" style={errorNote}>
            <span style={errorDot} />
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!ready}
          className="lm-si-amber"
          style={{
            minHeight: 48,
            marginTop: 4,
            border: 0,
            borderRadius: 10,
            font: 'inherit',
            fontSize: 15,
            fontWeight: 600,
            color: '#18181b',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            boxShadow: ready ? '0 6px 26px rgba(245,158,11,0.3)' : 'none',
            cursor: ready ? 'pointer' : 'not-allowed',
            opacity: ready ? 1 : 0.45,
          }}
        >
          {busy ? 'Checking…' : copy.cta}
        </button>

        <a
          href="#signin"
          className="lm-si-link"
          style={{ fontSize: 13 }}
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          Back to sign in
        </a>
      </form>
      )}
    </section>
  );
}

/**
 * GoTrue says "Token has expired or is invalid" for a mistyped code and for a code that
 * aged out, which are different problems with different answers — but it does not tell us
 * which, so neither does this. What it must not do is leave the operator staring at a
 * message with no next step.
 */
function humanise(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('failed to fetch') || m.includes('networkerror')) {
    return 'Cannot reach the authentication service. Check your connection and try again.';
  }
  if (m.includes('expired') || m.includes('invalid')) {
    return 'That code was not accepted. Check the digits, or ask your administrator to send a new invitation — codes expire with the link they came in.';
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts. Wait a minute before trying again.';
  }
  return humanError(new Error(message), 'That code could not be checked. Try again, or ask for a new email.');
}

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';

const h1 = {
  fontWeight: 600,
  fontSize: 'clamp(28px, 2.8vw, 38px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: '#fafafa',
} as const;

const sub = { fontSize: 15, lineHeight: 1.6, margin: '14px 0 0', color: '#a1a1aa' } as const;

const labelStyle = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.13em',
  textTransform: 'uppercase' as const,
  color: '#8b8b95',
} as const;

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: 46,
  padding: '0 13px',
  font: 'inherit',
  fontSize: 15,
  color: '#fafafa',
  background: '#12161f',
  border: '1px solid #283041',
  borderRadius: 10,
  outline: 'none',
} as const;

const noticeNote = {
  margin: '18px 0 0',
  padding: '11px 13px',
  fontSize: 13.5,
  lineHeight: 1.5,
  color: '#fcd34d',
  background: 'rgba(245,158,11,0.09)',
  border: '1px solid rgba(245,158,11,0.28)',
  borderRadius: 10,
} as const;

const errorNote = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  margin: 0,
  padding: '11px 13px',
  fontSize: 13.5,
  lineHeight: 1.5,
  color: '#fca5a5',
  background: 'rgba(248,113,113,0.09)',
  border: '1px solid rgba(248,113,113,0.28)',
  borderRadius: 10,
} as const;

const errorDot = {
  flex: 'none',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#f87171',
  boxShadow: '0 0 8px #f87171',
} as const;

function aberration(colour: string, dx: number, dy: number) {
  return {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colour,
    mixBlendMode: 'screen' as const,
    transform: `translate(${dx}px, ${dy}px)`,
  };
}
